import { useCallback, useEffect, useRef, useState } from "react";
import {
  Archive, Upload, Search, X, Loader2, Trash2, Save, CheckCircle2, XCircle,
  UploadCloud, ImageIcon, Eye, Sparkles, RotateCw, ShieldCheck, FileText,
  Layers, Play, StopCircle, AlertTriangle,
} from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import StatCard from "@/components/admin/StatCard";
import { adminApi } from "@/lib/adminApi";
import { useT } from "@/i18n/LanguageContext";

/**
 * StoicOs Content Vault — Founder Portal page.
 *
 * Permanent storage for every original StoicOs.ai asset.
 * "Create Once. Store Forever. Reuse Everywhere."
 *
 * Phase 1: JPG / PNG / WEBP images. Storage layer is format-agnostic
 * so MP4 / PDF / MP3 slot in without a redesign.
 */

const STATUS_TABS = [
  { key: "all",       labelKey: "admin.vault.tab.all" },
  { key: "draft",     labelKey: "admin.vault.tab.draft" },
  { key: "published", labelKey: "admin.vault.tab.published" },
  { key: "archived",  labelKey: "admin.vault.tab.archived" },
];

const STATUS_COLORS = {
  draft:     "border-gold/40 text-gold bg-gold/5",
  published: "border-emerald-400/40 text-emerald-300 bg-emerald-400/5",
  archived:  "border-white/20 text-bone/50 bg-white/5",
};

// Readiness pill styles keyed by the backend's `pill` code.
const READINESS_STYLES = {
  ready:         "border-emerald-400/50 text-emerald-300 bg-emerald-400/5",
  no_public_url: "border-amber-400/50 text-amber-300 bg-amber-400/5",
  upload:        "border-amber-400/50 text-amber-300 bg-amber-400/5",
  storage:       "border-red-400/50 text-red-300 bg-red-400/5",
  generic:       "border-white/20 text-bone/60 bg-white/5",
};

const ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";
const MAX_MB = 25;

const VIRTUES = ["Wisdom", "Justice", "Courage", "Temperance"];

function fmtSize(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusPill({ status }) {
  const cls = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span data-testid={`vault-status-${status}`} className={`inline-flex items-center border px-1.5 py-0.5 text-[9px] tracking-[0.24em] uppercase whitespace-nowrap ${cls}`}>
      {status}
    </span>
  );
}

/**
 * Small pill that mirrors the backend's `publish_readiness.pill` code
 * onto a coloured indicator. Rendered on grid cards AND in the detail
 * modal so the founder always knows whether a draft can be safely
 * flipped to Published (or why it can't).
 */
function ReadinessPill({ readiness, t, size = "sm" }) {
  if (!readiness) return null;
  const pill = readiness.pill || (readiness.ready ? "ready" : "generic");
  const cls = READINESS_STYLES[pill] || READINESS_STYLES.generic;
  const labelKey =
    pill === "ready"         ? "admin.vault.readiness.ready" :
    pill === "no_public_url" ? "admin.vault.readiness.no_public_url" :
    pill === "upload"        ? "admin.vault.readiness.upload_incomplete" :
    pill === "storage"       ? "admin.vault.readiness.storage_unreachable" :
                               "admin.vault.readiness.generic";
  // If the translation bundle is out of sync the raw key would leak — use
  // the backend's reason_label as a graceful fallback string instead.
  const translated = t(labelKey);
  const label = translated === labelKey ? (readiness.reason_label || pill) : translated;
  const Icon = readiness.ready ? CheckCircle2 : AlertTriangle;
  const px = size === "sm" ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-1 text-[10px]";
  return (
    <span
      data-testid={`vault-readiness-${pill}`}
      className={`inline-flex items-center gap-1 border ${px} tracking-[0.22em] uppercase whitespace-nowrap ${cls}`}
      title={readiness.reason_label || label}
    >
      <Icon size={size === "sm" ? 9 : 11} strokeWidth={1.75} /> {label}
    </span>
  );
}

/**
 * Publish-readiness section inside the detail modal. Renders:
 *  - the current pill (Ready / Missing URL / Upload incomplete …)
 *  - a checklist of the individual gates (content_id, storage_path, …)
 *  - a "Verify" button that triggers the deep readiness endpoint
 *    (probes storage) so the founder can pre-flight before publishing
 *  - a red banner when the last publish attempt was rejected, with the
 *    exact reason.
 *
 * All state comes from props — the parent owns fetching / caching so
 * this component stays purely presentational and easy to reason about.
 */
function ReadinessSection({ asset, publishErr, deep, verifying, onVerify, t }) {
  // Prefer the deep result (from /publish-readiness or the latest
  // PATCH response) — falls back to the cheap summary embedded in the
  // list. Both share the same shape.
  const readiness = deep || asset?.publish_readiness || null;
  const checks = readiness?.checks || {};
  const publicUrl = readiness?.public_url;

  return (
    <div className="pt-3 border-t border-white/8 space-y-2" data-testid="vault-readiness-section">
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-[10px] tracking-[0.28em] uppercase text-gold flex items-center gap-1.5">
          <ShieldCheck size={11} strokeWidth={1.75} /> {t("admin.vault.readiness.sectionTitle")}
        </p>
        <ReadinessPill readiness={readiness} t={t} size="md" />
      </div>

      {publishErr && (
        <div
          data-testid="vault-publish-error"
          className="border border-red-400/50 bg-red-400/5 text-[11px] text-red-200 px-2.5 py-2 space-y-1"
        >
          <p className="font-medium">
            {t("admin.vault.readiness.publishBlocked", {
              reason: publishErr.reason_code || "unknown",
            })}
          </p>
          {(publishErr.reasons || []).length > 0 && (
            <ul className="list-disc list-inside space-y-0.5 text-red-200/85">
              {(publishErr.reasons || []).map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Individual checks — only render if we have any check data */}
      {Object.keys(checks).length > 0 && (
        <ul className="text-[11px] text-bone/70 space-y-1" data-testid="vault-readiness-checks">
          {["has_content_id", "has_storage_path", "has_file_size", "upload_complete", "storage_reachable"]
            .filter((k) => k in checks)
            .map((k) => (
              <li key={k} className="flex items-center gap-2" data-testid={`vault-readiness-check-${k}`}>
                {checks[k] ? (
                  <CheckCircle2 size={11} strokeWidth={1.75} className="text-emerald-400" />
                ) : (
                  <XCircle size={11} strokeWidth={1.75} className="text-red-400" />
                )}
                <span className={checks[k] ? "text-bone/85" : "text-red-200"}>
                  {t(`admin.vault.readiness.check.${k}`)}
                </span>
              </li>
            ))}
        </ul>
      )}

      {publicUrl && (
        <div className="text-[10px] tracking-[0.2em] uppercase text-bone/45 pt-1">
          <span className="text-bone/40">{t("admin.vault.readiness.publicUrl")}:</span>{" "}
          <span
            data-testid="vault-readiness-public-url"
            className="text-bone/70 normal-case tracking-normal break-all"
          >
            {publicUrl}
          </span>
        </div>
      )}

      <button
        type="button"
        onClick={onVerify}
        disabled={verifying}
        data-testid="vault-readiness-verify"
        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-bone/70 border border-white/15 px-3 py-1.5 hover:text-gold hover:border-gold/40 disabled:opacity-40"
      >
        {verifying ? (
          <><Loader2 size={11} strokeWidth={1.75} className="animate-spin" /> {t("admin.vault.readiness.verifying")}</>
        ) : (
          <><ShieldCheck size={11} strokeWidth={1.75} /> {t("admin.vault.readiness.verify")}</>
        )}
      </button>
    </div>
  );
}

function UploadItem({ item }) {
  const { file, status, error, contentId } = item;
  const isDone = status === "done";
  const isErr = status === "error";
  return (
    <div
      data-testid={`vault-upload-item-${file.name}`}
      className={`flex items-center gap-3 px-3 py-2 border ${
        isErr ? "border-red-400/40 bg-red-400/5" : isDone ? "border-emerald-400/40 bg-emerald-400/5" : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="flex-shrink-0">
        {status === "queued"  && <span className="block w-3 h-3 border border-fog/50 rounded-full" />}
        {status === "uploading" && <Loader2 size={14} strokeWidth={1.75} className="text-gold animate-spin" />}
        {isDone && <CheckCircle2 size={14} strokeWidth={1.75} className="text-emerald-400" />}
        {isErr  && <XCircle size={14} strokeWidth={1.75} className="text-red-400" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-bone truncate">{file.name}</p>
        <p className="text-[10px] tracking-[0.22em] uppercase text-fog/70 mt-0.5">
          {fmtSize(file.size)}
          {contentId && <span className="ml-2 text-gold">· {contentId}</span>}
          {isErr && error && <span className="ml-2 text-red-300 normal-case tracking-normal">· {error}</span>}
        </p>
      </div>
    </div>
  );
}

function Chip({ children, testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center border border-gold/40 bg-gold/5 text-gold px-1.5 py-0.5 text-[10px] tracking-[0.18em] uppercase"
    >
      {children}
    </span>
  );
}

function SavedAiSummary({ a, t }) {
  const lines = [
    { k: "admin.vault.ai.field.titleEn", v: a.title_en },
    { k: "admin.vault.ai.field.titleNl", v: a.title_nl },
    { k: "admin.vault.ai.field.descriptionEn", v: a.description_en },
    { k: "admin.vault.ai.field.descriptionNl", v: a.description_nl },
  ];
  return (
    <div className="mt-3 border border-white/8 bg-white/[0.015] p-3 space-y-2" data-testid="vault-ai-saved">
      {lines.map((l) =>
        l.v ? (
          <div key={l.k}>
            <p className="text-[9px] tracking-[0.24em] uppercase text-bone/45">{t(l.k)}</p>
            <p className="text-[12px] text-bone/85 leading-relaxed mt-0.5">{l.v}</p>
          </div>
        ) : null
      )}
      {(a.tags || []).length > 0 && (
        <div>
          <p className="text-[9px] tracking-[0.24em] uppercase text-bone/45 mb-1">{t("admin.vault.ai.field.tags")}</p>
          <div className="flex flex-wrap gap-1">
            {a.tags.map((tag) => (
              <span key={tag} className="text-[10px] text-bone/70 bg-white/[0.04] px-1.5 py-0.5">{tag}</span>
            ))}
          </div>
        </div>
      )}
      {(a.virtues || []).length > 0 && (
        <div>
          <p className="text-[9px] tracking-[0.24em] uppercase text-bone/45 mb-1">{t("admin.vault.ai.field.virtues")}</p>
          <div className="flex flex-wrap gap-1">
            {a.virtues.map((v) => <Chip key={v}>{v}</Chip>)}
          </div>
        </div>
      )}
      {(a.themes || []).length > 0 && (
        <p className="text-[11px] text-bone/60"><span className="text-bone/40 tracking-[0.18em] uppercase text-[9px]">{t("admin.vault.ai.field.themes")}:</span> {a.themes.join(" · ")}</p>
      )}
      {(a.emotions || []).length > 0 && (
        <p className="text-[11px] text-bone/60"><span className="text-bone/40 tracking-[0.18em] uppercase text-[9px]">{t("admin.vault.ai.field.emotions")}:</span> {a.emotions.join(" · ")}</p>
      )}
    </div>
  );
}

function ProposalPanel({
  proposal, meta, promoteTitle, onPromoteToggle,
  onPatch, onToggleVirtue, onEditList,
  onDiscard, onSave, saving, err, t,
}) {
  return (
    <div className="border border-gold/40 bg-gold/[0.03] p-3 space-y-3" data-testid="vault-ai-proposal">
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[0.28em] uppercase text-gold">{t("admin.vault.ai.proposal.title")}</p>
        {meta && (
          <span className="text-[9px] tracking-[0.18em] uppercase text-bone/45">
            {meta.pages_analyzed} {meta.pages_analyzed === 1 ? t("admin.vault.ai.proposal.page") : t("admin.vault.ai.proposal.pages")} · {meta.model?.split("/").pop()}
          </span>
        )}
      </div>
      <p className="text-[10px] text-bone/55 italic leading-relaxed">{t("admin.vault.ai.proposal.hint")}</p>

      {meta && meta.brand_protected && meta.brand_protected.length > 0 && (
        <div className="border border-emerald-400/30 bg-emerald-400/[0.03] px-2 py-1.5" data-testid="vault-ai-brands">
          <p className="text-[9px] tracking-[0.22em] uppercase text-emerald-300 flex items-center gap-1.5">
            <ShieldCheck size={10} strokeWidth={2} /> {t("admin.vault.ai.proposal.brandProtected")}
          </p>
          <p className="text-[10px] text-bone/70 mt-0.5">{meta.brand_protected.join(" · ")}</p>
        </div>
      )}

      <FieldGroup label={t("admin.vault.ai.field.detectedLanguage")}>
        <span className="text-[11px] tracking-[0.22em] uppercase text-bone/80">{proposal.detected_language}</span>
      </FieldGroup>

      <ProposalInput
        label={t("admin.vault.ai.field.titleEn")} value={proposal.title_en}
        onChange={(v) => onPatch({ title_en: v })}
        testid="vault-ai-title-en"
      />
      <ProposalInput
        label={t("admin.vault.ai.field.titleNl")} value={proposal.title_nl}
        onChange={(v) => onPatch({ title_nl: v })}
        testid="vault-ai-title-nl"
      />
      <ProposalTextarea
        label={t("admin.vault.ai.field.descriptionEn")} value={proposal.description_en}
        onChange={(v) => onPatch({ description_en: v })}
        testid="vault-ai-desc-en"
      />
      <ProposalTextarea
        label={t("admin.vault.ai.field.descriptionNl")} value={proposal.description_nl}
        onChange={(v) => onPatch({ description_nl: v })}
        testid="vault-ai-desc-nl"
      />

      <FieldGroup label={t("admin.vault.ai.field.virtues")}>
        <div className="flex flex-wrap gap-1.5" data-testid="vault-ai-virtues">
          {VIRTUES.map((v) => {
            const on = (proposal.virtues || []).includes(v);
            return (
              <button
                key={v}
                type="button"
                onClick={() => onToggleVirtue(v)}
                data-testid={`vault-ai-virtue-${v.toLowerCase()}`}
                className={`px-2 py-1 text-[10px] tracking-[0.2em] uppercase border transition-colors ${
                  on ? "border-gold/60 text-gold bg-gold/10" : "border-white/15 text-bone/55 hover:text-bone"
                }`}
              >
                {v}
              </button>
            );
          })}
        </div>
      </FieldGroup>

      <ProposalCsv
        label={t("admin.vault.ai.field.tags")} value={(proposal.tags || []).join(", ")}
        onChange={(v) => onEditList("tags", v)}
        testid="vault-ai-tags"
      />
      <ProposalCsv
        label={t("admin.vault.ai.field.themes")} value={(proposal.themes || []).join(", ")}
        onChange={(v) => onEditList("themes", v)}
        testid="vault-ai-themes"
      />
      <ProposalCsv
        label={t("admin.vault.ai.field.emotions")} value={(proposal.emotions || []).join(", ")}
        onChange={(v) => onEditList("emotions", v)}
        testid="vault-ai-emotions"
      />

      {proposal.ocr_text ? (
        <details className="border border-white/10 bg-black/40 px-2 py-1.5">
          <summary className="text-[10px] tracking-[0.22em] uppercase text-bone/60 cursor-pointer flex items-center gap-1.5">
            <FileText size={10} strokeWidth={1.75} /> {t("admin.vault.ai.field.ocr")}
          </summary>
          <pre className="mt-2 text-[11px] text-bone/70 whitespace-pre-wrap font-mono max-h-40 overflow-y-auto" data-testid="vault-ai-ocr">
            {proposal.ocr_text}
          </pre>
        </details>
      ) : null}

      <label className="flex items-center gap-2 text-[11px] text-bone/70 cursor-pointer select-none pt-1">
        <input
          type="checkbox"
          checked={promoteTitle}
          onChange={onPromoteToggle}
          data-testid="vault-ai-promote-title"
          className="accent-gold"
        />
        {t("admin.vault.ai.proposal.promoteTitle")}
      </label>

      {err && (
        <p className="text-[11px] text-red-400" data-testid="vault-ai-save-err">{err}</p>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onDiscard}
          disabled={saving}
          data-testid="vault-ai-discard"
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-bone/60 border border-white/15 px-3 py-1.5 hover:text-bone disabled:opacity-40"
        >
          {t("admin.vault.ai.proposal.discard")}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          data-testid="vault-ai-confirm"
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-emerald-300 border border-emerald-400/40 bg-emerald-400/5 px-3 py-1.5 hover:bg-emerald-400/10 disabled:opacity-40"
        >
          {saving ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} strokeWidth={1.75} />}
          {t("admin.vault.ai.proposal.confirm")}
        </button>
      </div>
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div>
      <p className="text-[9px] tracking-[0.24em] uppercase text-bone/50 mb-1">{label}</p>
      {children}
    </div>
  );
}

function ProposalInput({ label, value, onChange, testid }) {
  return (
    <FieldGroup label={label}>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-black border border-white/15 text-bone px-2 py-1.5 text-[12px] focus:outline-none focus:border-gold"
      />
    </FieldGroup>
  );
}

function ProposalTextarea({ label, value, onChange, testid }) {
  return (
    <FieldGroup label={label}>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        rows={2}
        className="w-full bg-black border border-white/15 text-bone px-2 py-1.5 text-[12px] focus:outline-none focus:border-gold resize-y"
      />
    </FieldGroup>
  );
}

function ProposalCsv({ label, value, onChange, testid }) {
  return (
    <FieldGroup label={`${label} (comma-separated)`}>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-black border border-white/15 text-bone px-2 py-1.5 text-[12px] focus:outline-none focus:border-gold"
      />
    </FieldGroup>
  );
}

export default function AdminVault() {
  const { t } = useT();
  const fileInputRef = useRef(null);
  const gridEndRef = useRef(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // asset doc when the detail modal is open

  // upload state
  const [uploads, setUploads] = useState([]); // {file, status, error, contentId}
  const [dragActive, setDragActive] = useState(false);

  // Bulk AI enrichment (Phase 3a) — one background job at a time.
  // The button shows a live count of unprocessed assets. Kicking off a
  // job opens a polling modal that renders progress + log tail.
  const [unprocessed, setUnprocessed] = useState(0);
  const [bulkConfirm, setBulkConfirm] = useState(false);   // confirm dialog open?
  const [bulkJob, setBulkJob] = useState(null);            // full job doc while polling
  const [bulkErr, setBulkErr] = useState("");
  const bulkPollRef = useRef(null);

  // AI Enrichment (Phase 2) state — proposal panel inside the detail modal.
  // Held on the parent so we can invalidate it as the modal opens/closes.
  const [analyzing, setAnalyzing] = useState(false);
  const [aiErr, setAiErr] = useState("");
  const [proposal, setProposal] = useState(null); // {title_en, ..., ocr_text}
  const [proposalMeta, setProposalMeta] = useState(null); // {model, pages_analyzed, brand_protected}
  const [promoteTitle, setPromoteTitle] = useState(true);
  const [savingProposal, setSavingProposal] = useState(false);

  // Publish workflow — surfaces the exact reason a publish attempt was
  // rejected (backend HTTP 422) so the founder can fix the underlying
  // issue without guessing. Also holds the deep readiness result from
  // /publish-readiness so the modal can show which checks passed / failed.
  const [publishErr, setPublishErr] = useState(null); // structured {reason_code, reasons, checks} | null
  const [verifyingReadiness, setVerifyingReadiness] = useState(false);
  const [deepReadiness, setDeepReadiness] = useState(null); // full readiness payload

  const load = useCallback(async (reset = true) => {
    setErr("");
    if (reset) setLoading(true); else setLoadingMore(true);
    try {
      const params = { limit: 60 };
      if (status !== "all") params.status = status;
      if (search.trim()) params.search = search.trim();
      if (!reset && nextCursor) params.cursor = nextCursor;
      const d = await adminApi.listVault(params);
      setItems((prev) => (reset ? d.items : [...prev, ...d.items]));
      setNextCursor(d.next_cursor);
      setTotal(d.total || 0);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false); setLoadingMore(false);
    }
  }, [status, search, nextCursor]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setNextCursor(null); load(true); }, [status]);
  // Debounced search
  useEffect(() => {
    const id = setTimeout(() => { setNextCursor(null); load(true); }, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // ---- Unprocessed count (Bulk AI enrichment) ---------------------
  const refreshUnprocessed = useCallback(async () => {
    try {
      const d = await adminApi.vaultUnprocessedCount();
      setUnprocessed(d.unprocessed || 0);
    } catch { /* silent — the button will just show 0 */ }
  }, []);
  useEffect(() => { refreshUnprocessed(); }, [refreshUnprocessed]);

  // ---- Bulk analyze job polling -----------------------------------
  const stopBulkPolling = () => {
    if (bulkPollRef.current) {
      clearInterval(bulkPollRef.current);
      bulkPollRef.current = null;
    }
  };
  useEffect(() => () => stopBulkPolling(), []);

  const startBulkAnalyze = async () => {
    setBulkErr("");
    setBulkConfirm(false);
    try {
      const job = await adminApi.startVaultBatchAnalyze({ scope: "unprocessed", promoteTitle: true });
      setBulkJob(job);
      stopBulkPolling();
      bulkPollRef.current = setInterval(async () => {
        try {
          const fresh = await adminApi.getVaultJob(job.id);
          setBulkJob(fresh);
          if (["done", "error", "cancelled"].includes(fresh.status)) {
            stopBulkPolling();
            // Refresh grid + unprocessed count when the queue drains
            setNextCursor(null);
            load(true);
            refreshUnprocessed();
          }
        } catch (e) {
          setBulkErr((e.message || String(e)).slice(0, 200));
          stopBulkPolling();
        }
      }, 2000);
    } catch (e) {
      setBulkErr((e.message || String(e)).slice(0, 300));
    }
  };

  const cancelBulkAnalyze = async () => {
    if (!bulkJob?.id) return;
    try {
      const fresh = await adminApi.cancelVaultJob(bulkJob.id);
      setBulkJob(fresh);
    } catch (e) {
      setBulkErr((e.message || String(e)).slice(0, 200));
    }
  };

  const closeBulkModal = () => {
    stopBulkPolling();
    setBulkJob(null);
    setBulkErr("");
  };

  // ---- Upload -----------------------------------------------------
  const doUpload = async (files) => {
    if (!files || !files.length) return;
    const queued = Array.from(files).map((file) => ({
      file, status: "queued", error: "", contentId: null,
    }));
    setUploads((prev) => [...queued, ...prev].slice(0, 30));

    // Sequential — object storage prefers one at a time and this gives
    // clean per-file progress feedback.
    for (const item of queued) {
      setUploads((prev) => prev.map((u) => (u.file === item.file ? { ...u, status: "uploading" } : u)));
      try {
        const doc = await adminApi.uploadVault(item.file);
        setUploads((prev) => prev.map((u) => (u.file === item.file ? { ...u, status: "done", contentId: doc.content_id } : u)));
      } catch (e) {
        setUploads((prev) => prev.map((u) => (u.file === item.file ? { ...u, status: "error", error: (e.message || String(e)).slice(0, 160) } : u)));
      }
    }
    // Refresh the grid to show new items
    setNextCursor(null);
    load(true);
  };

  const onPick = (e) => { doUpload(e.target.files); e.target.value = ""; };
  const onDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const onDragLeave = () => setDragActive(false);
  const onDrop = (e) => { e.preventDefault(); setDragActive(false); doUpload(e.dataTransfer?.files); };

  // ---- Detail modal actions --------------------------------------
  const openDetail = (asset) => {
    setSelected({ ...asset, _titleDraft: asset.title });
    setProposal(null);
    setProposalMeta(null);
    setAiErr("");
    setAnalyzing(false);
    setPromoteTitle(true);
    setPublishErr(null);
    setDeepReadiness(asset.publish_readiness || null);
  };
  const closeDetail = () => {
    setSelected(null);
    setProposal(null);
    setProposalMeta(null);
    setAiErr("");
    setAnalyzing(false);
    setPublishErr(null);
    setDeepReadiness(null);
    setVerifyingReadiness(false);
  };

  // ---- AI Enrichment ---------------------------------------------
  const runAnalyze = async () => {
    if (!selected) return;
    setAiErr("");
    setAnalyzing(true);
    setProposal(null);
    setProposalMeta(null);
    try {
      const res = await adminApi.analyzeVaultAsset(selected.id);
      setProposal(res.proposal);
      setProposalMeta({
        model: res.model,
        pages_analyzed: res.pages_analyzed,
        brand_protected: res.brand_protected || [],
      });
    } catch (e) {
      setAiErr((e.message || String(e)).slice(0, 400));
    } finally {
      setAnalyzing(false);
    }
  };

  const discardProposal = () => {
    setProposal(null);
    setProposalMeta(null);
    setAiErr("");
  };

  const saveProposal = async () => {
    if (!selected || !proposal) return;
    setSavingProposal(true);
    try {
      const fresh = await adminApi.applyVaultAnalysis(selected.id, proposal, {
        promoteTitle,
        model: proposalMeta?.model || "",
      });
      setItems((prev) => prev.map((x) => (x.id === fresh.id ? fresh : x)));
      setSelected({ ...fresh, _titleDraft: fresh.title });
      setProposal(null);
      setProposalMeta(null);
      refreshUnprocessed();
    } catch (e) {
      setAiErr((e.message || String(e)).slice(0, 400));
    } finally {
      setSavingProposal(false);
    }
  };

  const patchProposal = (patch) => setProposal((p) => (p ? { ...p, ...patch } : p));
  const toggleVirtue = (v) => {
    if (!proposal) return;
    const set = new Set(proposal.virtues || []);
    if (set.has(v)) set.delete(v); else set.add(v);
    patchProposal({ virtues: VIRTUES.filter((x) => set.has(x)) });
  };
  const editList = (field, csv) =>
    patchProposal({ [field]: csv.split(",").map((s) => s.trim()).filter(Boolean) });

  const saveDetail = async () => {
    if (!selected) return;
    const patch = {};
    if (selected._titleDraft?.trim() && selected._titleDraft.trim() !== selected.title) patch.title = selected._titleDraft.trim();
    if (Object.keys(patch).length === 0) { closeDetail(); return; }
    try {
      const doc = await adminApi.patchVaultAsset(selected.id, patch);
      setItems((prev) => prev.map((x) => (x.id === doc.id ? doc : x)));
      closeDetail();
    } catch (e) { setErr(e.message || String(e)); }
  };
  const setDetailStatus = async (newStatus) => {
    if (!selected) return;
    setPublishErr(null);
    try {
      const doc = await adminApi.patchVaultAsset(selected.id, { status: newStatus });
      setItems((prev) => prev.map((x) => (x.id === doc.id ? doc : x)));
      setSelected({ ...doc, _titleDraft: doc.title });
      // Refresh deep readiness — a successful publish stamps public_url,
      // so what was "no_public_url" becomes "ready" instantly.
      if (doc.publish_readiness) setDeepReadiness(doc.publish_readiness);
    } catch (e) {
      // Structured publish failure? Show the reason banner in the modal.
      const d = e?.detail;
      if (e?.status === 422 && d && typeof d === "object" && d.error === "publish_not_ready") {
        setPublishErr({
          reason_code: d.reason_code,
          reasons: d.reasons || [],
          message: d.message || t("admin.vault.readiness.publishBlocked", { reason: d.reason_code || "unknown" }),
          checks: d.checks || {},
        });
        return;
      }
      // Post-publish visibility rollback — the asset was written as
      // published but the Site Media picker couldn't find it, so the
      // backend rolled it back to draft. Surface the diagnostic so
      // the founder can investigate on production.
      if (e?.status === 500 && d && typeof d === "object" && d.error === "publish_invisible") {
        setPublishErr({
          reason_code: d.reason_code || "publish_invisible",
          reasons: [d.message, ...(d.details?.mismatch_reasons || [])].filter(Boolean),
          message: d.message,
          checks: {},
        });
        return;
      }
      setErr(e.message || String(e));
    }
  };

  const verifyReadiness = async () => {
    if (!selected) return;
    setVerifyingReadiness(true);
    setPublishErr(null);
    try {
      const r = await adminApi.vaultPublishReadiness(selected.id);
      setDeepReadiness(r);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setVerifyingReadiness(false);
    }
  };
  const deleteSelected = async () => {
    if (!selected) return;
    if (!window.confirm(t("admin.vault.confirm.delete", { title: selected.title }))) return;
    try {
      await adminApi.deleteVaultAsset(selected.id);
      setItems((prev) => prev.filter((x) => x.id !== selected.id));
      setTotal((n) => Math.max(0, n - 1));
      closeDetail();
    } catch (e) { setErr(e.message || String(e)); }
  };

  return (
    <AdminShell active="vault">
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Header */}
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-[11px] tracking-[0.32em] uppercase text-gold">{t("admin.vault.eyebrow")}</p>
            <h1 className="font-serif text-3xl md:text-4xl text-bone mt-2 flex items-center gap-3" data-testid="admin-vault-title">
              <Archive size={22} strokeWidth={1.5} className="text-gold" />
              {t("admin.vault.title")}
            </h1>
            <p className="mt-2 text-sm text-bone/65 max-w-2xl leading-relaxed italic">{t("admin.vault.tagline")}</p>
          </div>
          <div className="flex items-center gap-6">
            <button
              type="button"
              onClick={() => { setBulkErr(""); setBulkConfirm(true); }}
              disabled={unprocessed === 0 || !!bulkJob}
              data-testid="vault-bulk-analyze"
              className={`inline-flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase border px-3.5 py-2 transition-colors ${
                unprocessed === 0
                  ? "border-white/10 text-bone/30 cursor-not-allowed"
                  : "border-gold/60 text-gold hover:bg-gold/10"
              }`}
              title={t("admin.vault.bulk.buttonHint")}
            >
              <Layers size={12} strokeWidth={1.75} />
              {t("admin.vault.bulk.button")}
              {unprocessed > 0 && (
                <span className="ml-1 text-[10px] tracking-normal font-medium bg-gold/15 border border-gold/40 px-1.5 py-0.5" data-testid="vault-unprocessed-count">
                  {unprocessed}
                </span>
              )}
            </button>
            <div className="text-right">
              <p className="text-[10px] tracking-[0.32em] uppercase text-bone/50">{t("admin.vault.stat.total")}</p>
              <p className="font-serif text-3xl text-gold mt-1" data-testid="vault-total">{total}</p>
            </div>
          </div>
        </header>

        {/* Upload zone */}
        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          data-testid="vault-dropzone"
          className={`border-2 border-dashed transition-colors p-6 md:p-10 text-center ${
            dragActive ? "border-gold bg-gold/5" : "border-white/15 bg-white/[0.02]"
          }`}
        >
          <UploadCloud size={28} strokeWidth={1.5} className="text-gold mx-auto" />
          <p className="mt-3 font-serif text-lg text-bone">{t("admin.vault.upload.title")}</p>
          <p className="mt-1 text-sm text-bone/60">{t("admin.vault.upload.subtitle")}</p>
          <p className="mt-2 text-[10px] tracking-[0.22em] uppercase text-bone/40">
            {t("admin.vault.upload.formats", { max: MAX_MB })}
          </p>
          <div className="mt-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="vault-select-files"
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-gold border border-gold/60 px-4 py-2 hover:bg-gold/10"
            >
              <Upload size={12} strokeWidth={1.75} /> {t("admin.vault.upload.button")}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={onPick}
              data-testid="vault-file-input"
            />
          </div>
        </div>

        {/* Active uploads list */}
        {uploads.length > 0 && (
          <div className="space-y-2" data-testid="vault-uploads">
            {uploads.map((u, i) => <UploadItem key={u.file.name + i} item={u} />)}
          </div>
        )}

        {/* Filter row */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex border border-white/15 divide-x divide-white/10">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setStatus(tab.key)}
                data-testid={`vault-tab-${tab.key}`}
                className={`px-3 py-1.5 text-[10px] tracking-[0.28em] uppercase transition-colors ${
                  status === tab.key ? "bg-gold/10 text-gold" : "text-bone/60 hover:text-bone"
                }`}
              >
                {t(tab.labelKey)}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px] max-w-[400px]">
            <Search size={13} strokeWidth={1.5} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-bone/50" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("admin.vault.searchPlaceholder")}
              data-testid="admin-vault-search"
              className="w-full bg-black border border-white/15 text-bone pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:border-gold"
            />
          </div>
        </div>

        {err && <p className="text-sm text-red-400" data-testid="admin-vault-err">{err}</p>}

        {/* Grid */}
        {loading ? (
          <p className="p-6 text-sm text-bone/50 flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> {t("admin.label.loading")}</p>
        ) : items.length === 0 ? (
          <div className="border border-white/10 bg-white/[0.02] p-10 text-center" data-testid="vault-empty">
            <ImageIcon size={26} strokeWidth={1.25} className="text-bone/30 mx-auto" />
            <p className="mt-3 font-serif text-lg text-bone/80">{t("admin.vault.empty.title")}</p>
            <p className="mt-1 text-sm text-bone/50 italic">{t("admin.vault.empty.body")}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4" data-testid="vault-grid">
              {items.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => openDetail(a)}
                  data-testid={`vault-card-${a.content_id}`}
                  className="group text-left border border-white/10 bg-white/[0.02] hover:border-gold/50 transition-colors"
                >
                  <div className="aspect-square bg-black/50 relative overflow-hidden">
                    <img
                      src={adminApi.vaultFileUrl(a.id, "thumb")}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-1.5 right-1.5"><StatusPill status={a.status} /></div>
                    {a.publish_readiness && !a.publish_readiness.ready && (
                      <div className="absolute bottom-1.5 left-1.5">
                        <ReadinessPill readiness={a.publish_readiness} t={t} />
                      </div>
                    )}
                  </div>
                  <div className="p-2.5">
                    <p className="text-[10px] tracking-[0.24em] uppercase text-gold">{a.content_id}</p>
                    <p className="mt-1 font-serif text-[13px] text-bone/90 truncate">{a.title}</p>
                    <p className="mt-1 text-[10px] tracking-[0.2em] uppercase text-bone/45">
                      {new Date(a.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            {nextCursor && (
              <div ref={gridEndRef} className="pt-2 text-center">
                <button
                  type="button"
                  onClick={() => load(false)}
                  disabled={loadingMore}
                  data-testid="vault-load-more"
                  className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-bone/70 border border-white/15 px-4 py-2 hover:text-gold hover:border-gold/40 disabled:opacity-40"
                >
                  {loadingMore ? <Loader2 size={12} className="animate-spin" /> : null}
                  {t("admin.vault.loadMore")}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeDetail} data-testid="vault-detail-modal">
          <div className="bg-ink border border-gold/40 max-w-4xl w-full max-h-[92vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <p className="text-[10px] tracking-[0.28em] uppercase text-gold">{selected.content_id}</p>
                <h3 className="font-serif text-xl text-bone mt-1 truncate max-w-xl">{selected.title}</h3>
              </div>
              <button type="button" onClick={closeDetail} className="p-2 text-bone/60 hover:text-bone" data-testid="vault-detail-close"><X size={18} strokeWidth={1.5} /></button>
            </div>
            <div className="flex-1 overflow-y-auto grid md:grid-cols-[1fr_320px] gap-0">
              <div className="bg-black/60 flex items-center justify-center min-h-[240px] max-h-[70vh] p-3">
                <img
                  src={adminApi.vaultFileUrl(selected.id, "thumb")}
                  alt={selected.title}
                  data-testid="vault-detail-image"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="px-5 py-5 space-y-4 border-l border-white/8">
                <div>
                  <label className="block text-[10px] tracking-[0.28em] uppercase text-bone/70 mb-2">{t("admin.vault.field.title")}</label>
                  <input
                    type="text"
                    value={selected._titleDraft || ""}
                    onChange={(e) => setSelected({ ...selected, _titleDraft: e.target.value })}
                    data-testid="vault-detail-title"
                    className="w-full bg-black border border-gold/30 text-bone px-3 py-2 focus:outline-none focus:border-gold"
                  />
                </div>
                <div className="space-y-1 text-[11px] tracking-[0.18em] uppercase text-bone/55">
                  <p><span className="text-bone/40">{t("admin.vault.field.author")}:</span> <span className="text-bone/80 normal-case tracking-normal">{selected.author}</span></p>
                  <p><span className="text-bone/40">{t("admin.vault.field.uploaded")}:</span> <span className="text-bone/80 normal-case tracking-normal">{new Date(selected.created_at).toLocaleString()}</span></p>
                  <p><span className="text-bone/40">{t("admin.vault.field.filename")}:</span> <span className="text-bone/80 normal-case tracking-normal truncate">{selected.original_filename}</span></p>
                  <p><span className="text-bone/40">{t("admin.vault.field.type")}:</span> <span className="text-bone/80 normal-case tracking-normal">{selected.file_type}</span></p>
                  <p><span className="text-bone/40">{t("admin.vault.field.size")}:</span> <span className="text-bone/80 normal-case tracking-normal">{fmtSize(selected.file_size)}</span></p>
                  <p><span className="text-bone/40">{t("admin.vault.field.dims")}:</span> <span className="text-bone/80 normal-case tracking-normal">{selected.width} × {selected.height}</span></p>
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.28em] uppercase text-bone/70 mb-2">{t("admin.vault.field.status")}</label>
                  <div className="flex flex-wrap gap-1.5">
                    {["draft", "published", "archived"].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setDetailStatus(s)}
                        data-testid={`vault-status-set-${s}`}
                        className={`px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase border transition-colors ${
                          selected.status === s ? STATUS_COLORS[s] : "border-white/15 text-bone/60 hover:text-bone"
                        }`}
                      >
                        {t(`admin.vault.tab.${s}`)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Publish readiness — Phase 3b-2 workflow guard --------- */}
                <ReadinessSection
                  asset={selected}
                  publishErr={publishErr}
                  deep={deepReadiness}
                  verifying={verifyingReadiness}
                  onVerify={verifyReadiness}
                  t={t}
                />

                {/* AI Enrichment (Phase 2) ---------------------------- */}
                <div className="pt-3 border-t border-white/8" data-testid="vault-ai-section">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] tracking-[0.28em] uppercase text-gold flex items-center gap-1.5">
                      <Sparkles size={11} strokeWidth={1.75} /> {t("admin.vault.ai.title")}
                    </p>
                    {selected.ai_status === "confirmed" && (
                      <span className="text-[9px] tracking-[0.22em] uppercase text-emerald-300 border border-emerald-400/40 bg-emerald-400/5 px-1.5 py-0.5" data-testid="vault-ai-confirmed-badge">
                        {t("admin.vault.ai.confirmed")}
                        {selected.ai_confirmed_by === "bulk" && (
                          <span className="ml-1 opacity-70">· bulk</span>
                        )}
                      </span>
                    )}
                  </div>

                  {/* Idle state: show the "Analyseer met AI" button OR re-run */}
                  {!proposal && !analyzing && (
                    <div className="space-y-2">
                      <p className="text-[11px] text-bone/55 italic leading-relaxed">
                        {t("admin.vault.ai.intro")}
                      </p>
                      <button
                        type="button"
                        onClick={runAnalyze}
                        data-testid="vault-ai-analyze"
                        className="w-full inline-flex items-center justify-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-gold border border-gold/60 px-3 py-2 hover:bg-gold/10"
                      >
                        {selected.ai_status === "confirmed" ? (
                          <><RotateCw size={11} strokeWidth={1.75} /> {t("admin.vault.ai.reanalyze")}</>
                        ) : (
                          <><Sparkles size={11} strokeWidth={1.75} /> {t("admin.vault.ai.analyze")}</>
                        )}
                      </button>
                      {aiErr && (
                        <p className="text-[11px] text-red-400 leading-relaxed" data-testid="vault-ai-err">
                          {aiErr}
                        </p>
                      )}

                      {selected.ai_status === "confirmed" && selected.ai_analysis && (
                        <SavedAiSummary a={selected.ai_analysis} t={t} />
                      )}
                    </div>
                  )}

                  {/* Loading */}
                  {analyzing && (
                    <div className="border border-gold/30 bg-gold/[0.03] px-3 py-6 text-center" data-testid="vault-ai-loading">
                      <Loader2 size={16} className="text-gold animate-spin mx-auto" />
                      <p className="mt-2 text-[11px] text-bone/70 tracking-[0.18em] uppercase">{t("admin.vault.ai.working")}</p>
                      <p className="mt-1 text-[10px] text-bone/50 italic">{t("admin.vault.ai.workingHint")}</p>
                    </div>
                  )}

                  {/* Proposal review */}
                  {proposal && !analyzing && (
                    <ProposalPanel
                      proposal={proposal}
                      meta={proposalMeta}
                      promoteTitle={promoteTitle}
                      onPromoteToggle={() => setPromoteTitle((v) => !v)}
                      onPatch={patchProposal}
                      onToggleVirtue={toggleVirtue}
                      onEditList={editList}
                      onDiscard={discardProposal}
                      onSave={saveProposal}
                      saving={savingProposal}
                      err={aiErr}
                      t={t}
                    />
                  )}
                </div>

                {/* Used In (Phase 3b-1) */}
                <UsedInSection assetId={selected.id} contentId={selected.content_id} t={t} />
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={deleteSelected}
                  data-testid="vault-detail-delete"
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-bone/70 border border-white/20 px-3 py-1.5 hover:text-red-400 hover:border-red-400/40"
                >
                  <Trash2 size={11} strokeWidth={1.75} /> {t("admin.action.delete")}
                </button>
                <label className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-bone/70 border border-white/20 px-3 py-1.5 hover:text-gold hover:border-gold/40 cursor-pointer" data-testid="vault-detail-replace-btn">
                  <UploadCloud size={11} strokeWidth={1.75} /> {t("admin.vault.action.replace")}
                  <input
                    type="file"
                    accept={ACCEPT}
                    className="hidden"
                    data-testid="vault-detail-replace-input"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (!f) return;
                      if (!confirm(t("admin.vault.replace.confirm"))) return;
                      try {
                        const fresh = await adminApi.replaceVaultBytes(selected.id, f);
                        setItems((prev) => prev.map((x) => (x.id === fresh.id ? fresh : x)));
                        setSelected({ ...fresh, _titleDraft: fresh.title });
                      } catch (err) {
                        alert((err.message || String(err)).slice(0, 300));
                      }
                    }}
                  />
                </label>
              </div>
              <div className="flex items-center gap-2">
                <a href={adminApi.vaultFileUrl(selected.id, "original")} target="_blank" rel="noreferrer" data-testid="vault-detail-open-original" className="text-[10px] tracking-[0.24em] uppercase text-bone/70 border border-white/20 px-3 py-1.5 hover:text-bone inline-flex items-center gap-1">
                  <Eye size={11} strokeWidth={1.75} /> {t("admin.vault.action.viewOriginal")}
                </a>
                <button type="button" onClick={saveDetail} data-testid="vault-detail-save" className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-gold border border-gold/60 px-3 py-1.5 hover:bg-gold/10">
                  <Save size={11} strokeWidth={1.75} /> {t("admin.action.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- Bulk AI Enrichment — confirm dialog ---- */}
      {bulkConfirm && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setBulkConfirm(false)}>
          <div
            className="relative max-w-md w-full border border-gold/40 bg-[#0A0A0A]"
            onClick={(e) => e.stopPropagation()}
            data-testid="vault-bulk-confirm-modal"
          >
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-2 text-gold">
                <Layers size={18} strokeWidth={1.5} />
                <h3 className="font-serif text-xl">{t("admin.vault.bulk.confirmTitle")}</h3>
              </div>
              <p className="text-sm text-bone/80 leading-relaxed">
                {t("admin.vault.bulk.confirmBody", { n: unprocessed })}
              </p>
              <div className="border border-amber-400/30 bg-amber-400/[0.04] px-3 py-2 flex items-start gap-2">
                <AlertTriangle size={13} strokeWidth={1.75} className="text-amber-300 mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-bone/70 leading-relaxed">{t("admin.vault.bulk.confirmWarning")}</p>
              </div>
              {bulkErr && <p className="text-[11px] text-red-400" data-testid="vault-bulk-err">{bulkErr}</p>}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setBulkConfirm(false)}
                  data-testid="vault-bulk-cancel"
                  className="text-[10px] tracking-[0.24em] uppercase text-bone/60 border border-white/15 px-3 py-1.5 hover:text-bone"
                >
                  {t("admin.action.cancel")}
                </button>
                <button
                  type="button"
                  onClick={startBulkAnalyze}
                  data-testid="vault-bulk-start"
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-gold border border-gold/60 px-3 py-1.5 hover:bg-gold/10"
                >
                  <Play size={11} strokeWidth={1.75} /> {t("admin.vault.bulk.start")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- Bulk AI Enrichment — progress modal ---- */}
      {bulkJob && (
        <BulkJobModal
          job={bulkJob}
          err={bulkErr}
          onCancel={cancelBulkAnalyze}
          onClose={closeBulkModal}
          t={t}
        />
      )}
    </AdminShell>
  );
}

function BulkJobModal({ job, err, onCancel, onClose, t }) {
  const total = Math.max(job.total || 0, 1);
  const processed = (job.done || 0) + (job.failed || 0) + (job.skipped || 0);
  const pct = Math.min(100, Math.round((processed / total) * 100));
  const finished = ["done", "error", "cancelled"].includes(job.status);
  const logTail = (job.log || []).slice(-40).reverse();

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" data-testid="vault-bulk-progress-modal">
      <div className="relative max-w-2xl w-full max-h-[90vh] border border-gold/40 bg-[#0A0A0A] flex flex-col">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2 text-gold">
            <Layers size={16} strokeWidth={1.5} />
            <h3 className="font-serif text-lg">{t("admin.vault.bulk.progressTitle")}</h3>
            <span
              className={`ml-2 text-[9px] tracking-[0.24em] uppercase px-1.5 py-0.5 border ${
                job.status === "done" ? "border-emerald-400/40 text-emerald-300 bg-emerald-400/5"
                : job.status === "error" ? "border-red-400/40 text-red-300 bg-red-400/5"
                : job.status === "cancelled" ? "border-white/20 text-bone/60"
                : "border-gold/40 text-gold bg-gold/5"
              }`}
              data-testid="vault-bulk-status"
            >
              {job.status}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={!finished}
            className="text-bone/50 hover:text-bone disabled:opacity-30"
            data-testid="vault-bulk-close"
            title={finished ? t("admin.action.close") : t("admin.vault.bulk.closeDisabled")}
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto">
          <div>
            <div className="flex items-center justify-between text-[11px] text-bone/70 mb-1.5">
              <span data-testid="vault-bulk-progress-text">
                {processed} / {job.total || 0} {t("admin.vault.bulk.processed")}
              </span>
              <span className="text-gold font-medium">{pct}%</span>
            </div>
            <div className="h-2 bg-white/[0.06] border border-white/10">
              <div
                className={`h-full transition-all duration-500 ${
                  job.status === "error" ? "bg-red-400/60" : "bg-gold"
                }`}
                style={{ width: `${pct}%` }}
                data-testid="vault-bulk-progress-bar"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <StatPill label={t("admin.vault.bulk.done")} value={job.done || 0} color="emerald" testid="vault-bulk-done" />
            <StatPill label={t("admin.vault.bulk.failed")} value={job.failed || 0} color="red" testid="vault-bulk-failed" />
            <StatPill label={t("admin.vault.bulk.skipped")} value={job.skipped || 0} color="bone" testid="vault-bulk-skipped" />
          </div>

          {job.error && (
            <p className="text-[11px] text-red-300 border border-red-400/30 bg-red-400/[0.03] px-2 py-1.5" data-testid="vault-bulk-fatal-err">
              {job.error}
            </p>
          )}
          {err && (
            <p className="text-[11px] text-red-300">{err}</p>
          )}

          {/* Log tail */}
          <div className="border border-white/10 bg-black/60 max-h-64 overflow-y-auto">
            <p className="text-[9px] tracking-[0.22em] uppercase text-bone/45 px-3 pt-2">{t("admin.vault.bulk.log")}</p>
            <ul className="text-[11px] font-mono divide-y divide-white/5" data-testid="vault-bulk-log">
              {logTail.map((l, i) => (
                <li key={i} className="px-3 py-1.5 flex items-start gap-2">
                  <LogStatusDot status={l.status} />
                  <span className="text-bone/45 flex-shrink-0 tabular-nums text-[10px]">
                    {(l.ts || "").slice(11, 19)}
                  </span>
                  {l.content_id && (
                    <span className="text-gold flex-shrink-0">{l.content_id}</span>
                  )}
                  <span className="text-bone/70 truncate">{l.message || ""}</span>
                </li>
              ))}
              {logTail.length === 0 && (
                <li className="px-3 py-2 text-bone/40 italic">{t("admin.vault.bulk.logEmpty")}</li>
              )}
            </ul>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-white/10 flex items-center justify-end gap-2">
          {!finished ? (
            <button
              type="button"
              onClick={onCancel}
              data-testid="vault-bulk-cancel-job"
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-bone/60 border border-white/15 px-3 py-1.5 hover:text-red-400 hover:border-red-400/40"
            >
              <StopCircle size={11} strokeWidth={1.75} /> {t("admin.vault.bulk.cancelJob")}
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              data-testid="vault-bulk-close-final"
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-gold border border-gold/60 px-3 py-1.5 hover:bg-gold/10"
            >
              {t("admin.action.close")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatPill({ label, value, color, testid }) {
  const colors = {
    emerald: "border-emerald-400/40 text-emerald-300 bg-emerald-400/5",
    red: "border-red-400/40 text-red-300 bg-red-400/5",
    bone: "border-white/15 text-bone/70 bg-white/[0.02]",
  };
  return (
    <div className={`border px-3 py-2 ${colors[color] || colors.bone}`} data-testid={testid}>
      <p className="text-[9px] tracking-[0.24em] uppercase opacity-70">{label}</p>
      <p className="font-serif text-2xl mt-0.5 tabular-nums">{value}</p>
    </div>
  );
}

function LogStatusDot({ status }) {
  const map = {
    ok: "bg-emerald-400",
    failed: "bg-red-400",
    skipped: "bg-white/30",
    started: "bg-gold",
    queued: "bg-gold/50",
    done: "bg-emerald-400",
    cancelled: "bg-white/40",
  };
  const cls = map[status] || "bg-white/30";
  return <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${cls}`} />;
}

function UsedInSection({ assetId, contentId, t }) {
  const [state, setState] = useState({ loading: true, data: null, err: "" });
  const [integrity, setIntegrity] = useState(null); // {ok, expected_size, actual_size, hint}
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, err: "" });
    setIntegrity(null);
    adminApi.vaultUsage(assetId)
      .then((d) => { if (!cancelled) setState({ loading: false, data: d, err: "" }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, data: null, err: e.message || String(e) }); });
    return () => { cancelled = true; };
  }, [assetId]);

  const runIntegrityCheck = async () => {
    setChecking(true);
    try {
      const r = await adminApi.vaultAssetIntegrity(assetId);
      setIntegrity(r);
    } catch (e) {
      setIntegrity({ ok: false, hint: e.message || String(e), expected_size: null, actual_size: null });
    } finally {
      setChecking(false);
    }
  };

  const { loading, data, err } = state;
  return (
    <div className="pt-3 border-t border-white/8" data-testid="vault-used-in">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] tracking-[0.28em] uppercase text-bone/60 flex items-center gap-1.5">
          <Layers size={11} strokeWidth={1.75} /> {t("admin.vault.usedIn.title")}
        </p>
        <span className="text-[9px] tracking-[0.22em] uppercase text-bone/40">
          {contentId}
        </span>
      </div>

      {/* Integrity check — surfaces cross-environment storage collisions */}
      <div className="mb-3 flex items-center gap-2" data-testid="vault-integrity-row">
        <button
          type="button"
          onClick={runIntegrityCheck}
          disabled={checking}
          data-testid="vault-integrity-check"
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-bone/70 border border-white/15 px-2 py-1 hover:text-gold hover:border-gold/40 disabled:opacity-40"
        >
          {checking ? <Loader2 size={10} className="animate-spin" /> : <ShieldCheck size={10} />}
          {t("admin.vault.integrity.check")}
        </button>
        {integrity && (
          integrity.ok ? (
            <span className="text-[10px] tracking-[0.22em] uppercase text-emerald-300 border border-emerald-400/40 bg-emerald-400/5 px-1.5 py-1" data-testid="vault-integrity-ok">
              ✓ {t("admin.vault.integrity.ok")} · {(integrity.actual_size / 1024).toFixed(1)} KB
            </span>
          ) : (
            <span className="text-[10px] tracking-[0.22em] uppercase text-red-300 border border-red-400/40 bg-red-400/5 px-1.5 py-1" data-testid="vault-integrity-fail">
              ✗ {t("admin.vault.integrity.fail")}
            </span>
          )
        )}
      </div>
      {integrity && !integrity.ok && integrity.hint && (
        <p className="text-[11px] text-red-300/90 mb-3 leading-relaxed" data-testid="vault-integrity-hint">
          {integrity.hint}
        </p>
      )}

      {loading && (
        <div className="text-[11px] text-bone/40 italic flex items-center gap-1.5">
          <Loader2 size={10} className="animate-spin" /> {t("admin.vault.usedIn.loading")}
        </div>
      )}
      {err && <p className="text-[11px] text-red-400">{err}</p>}
      {data && !loading && (
        <div className="space-y-2">
          {data.total === 0 ? (
            <p className="text-[11px] text-bone/50 italic">{t("admin.vault.usedIn.empty")}</p>
          ) : (
            <p className="text-[11px] text-bone/70" data-testid="vault-used-total">
              {t("admin.vault.usedIn.summary", { n: data.total })}
            </p>
          )}
          {data.journal && data.journal.length > 0 && (
            <div>
              <p className="text-[9px] tracking-[0.22em] uppercase text-bone/45 mb-1">
                Journal <span className="text-gold">({data.journal.length})</span>
              </p>
              <ul className="text-[11px] space-y-0.5" data-testid="vault-used-journal">
                {data.journal.map((j) => (
                  <li key={j.id} className="flex items-center gap-2">
                    <span className={`w-1 h-1 rounded-full ${j.status === "published" ? "bg-emerald-400" : "bg-bone/30"}`} />
                    <a
                      href={`/admin/journal?edit=${j.id}`}
                      className="text-bone/80 hover:text-gold truncate"
                      data-testid={`vault-used-journal-${j.id}`}
                    >
                      {j.title || j.slug}
                    </a>
                    <span className="text-[9px] tracking-[0.18em] uppercase text-bone/40 ml-auto flex-shrink-0">{j.status}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
